import { ItemTypes } from "../ts/enums";
import {
  ISettingsItem,
  ITaskItem,
  IPenaltyItem,
  IPermissionItem,
  IEventItem,
  ISessionSettings,
  ITaskSettings,
  IPenaltySettings,
  IPermissionSettings,
  IEventSettings,
  IPositionSettings,
  TaskItem,
  PenaltyItem,
  PermissionItem,
  EventItem,
  PositionItem,
  BaseItem,
} from "../ts/Definitions";
import { AuditService } from "./AuditService";
import { v1 as uuid } from "uuid";

interface ItemTypeConfig<T extends ISettingsItem> {
  itemType: ItemTypes;
  getCollection: (settings: ISessionSettings) => T[];
  setCollection: (settings: ISessionSettings, items: T[]) => void;
  createInstance: (item: T) => BaseItem;
  getSettingsSection: (settings: ISessionSettings) => any;
  auditNameSingular: string;
}

export class ItemRepository {
  private auditService: AuditService;
  private isSystemSettings: () => boolean;
  private getCurrentItem: (item: ISettingsItem) => ISettingsItem | undefined;
  private validateItem: (item: ISettingsItem, bypassOwnerCheck?: boolean) => boolean;
  private fireListeners: () => void;

  constructor(
    auditService: AuditService,
    isSystemSettings: () => boolean,
    getCurrentItem: (item: ISettingsItem) => ISettingsItem | undefined,
    validateItem: (item: ISettingsItem, bypassOwnerCheck?: boolean) => boolean,
    fireListeners: () => void
  ) {
    this.auditService = auditService;
    this.isSystemSettings = isSystemSettings;
    this.getCurrentItem = getCurrentItem;
    this.validateItem = validateItem;
    this.fireListeners = fireListeners;
  }

  /**
   * Get configuration for a specific item type
   */
  private getItemConfig(itemType: ItemTypes): ItemTypeConfig<any> {
    switch (itemType) {
      case ItemTypes.task:
        return {
          itemType: ItemTypes.task,
          getCollection: (s) => s.creditSettings?.credits || [],
          setCollection: (s, items) => {
            if (!s.creditSettings) s.creditSettings = {} as ITaskSettings;
            s.creditSettings.credits = items;
          },
          createInstance: (item) => new TaskItem(item as ITaskItem),
          getSettingsSection: (s) => s.creditSettings,
          auditNameSingular: "Task",
        };
      case ItemTypes.penalty:
        return {
          itemType: ItemTypes.penalty,
          getCollection: (s) => s.penaltySettings?.penalties || [],
          setCollection: (s, items) => {
            if (!s.penaltySettings) s.penaltySettings = {} as IPenaltySettings;
            s.penaltySettings.penalties = items;
          },
          createInstance: (item) => new PenaltyItem(item as IPenaltyItem),
          getSettingsSection: (s) => s.penaltySettings,
          auditNameSingular: "Penalty",
        };
      case ItemTypes.permission:
        return {
          itemType: ItemTypes.permission,
          getCollection: (s) => s.permissionSettings?.permissions || [],
          setCollection: (s, items) => {
            if (!s.permissionSettings) s.permissionSettings = {} as IPermissionSettings;
            s.permissionSettings.permissions = items;
          },
          createInstance: (item) => new PermissionItem(item as IPermissionItem),
          getSettingsSection: (s) => s.permissionSettings,
          auditNameSingular: "Permission",
        };
      case ItemTypes.event:
        return {
          itemType: ItemTypes.event,
          getCollection: (s) => s.eventsSettings?.events || [],
          setCollection: (s, items) => {
            if (!s.eventsSettings) s.eventsSettings = {} as IEventSettings;
            s.eventsSettings.events = items;
          },
          createInstance: (item) => new EventItem(item as IEventItem),
          getSettingsSection: (s) => s.eventsSettings,
          auditNameSingular: "Event",
        };
      case ItemTypes.position:
        return {
          itemType: ItemTypes.position,
          getCollection: (s) => s.positionSettings?.positions || [],
          setCollection: (s, items) => {
            if (!s.positionSettings) s.positionSettings = {} as IPositionSettings;
            s.positionSettings.positions = items;
          },
          createInstance: (item) => new PositionItem(item as ISettingsItem),
          getSettingsSection: (s) => s.positionSettings,
          auditNameSingular: "Position",
        };
      default:
        throw new Error(`Unsupported item type: ${itemType}`);
    }
  }

  /**
   * Create or update an item
   */
  public upsertItem<T extends ISettingsItem>(
    settings: ISessionSettings,
    item: T,
    bypassSettingOwnerCheck?: boolean,
    suppressAutoSave?: boolean
  ): { success: boolean; requiresSave: boolean } {
    if (!this.validateItem(item, bypassSettingOwnerCheck)) {
      return { success: false, requiresSave: false };
    }

    const isNew = !item.key;
    if (isNew) {
      item.key = uuid();
    }

    // Set common properties
    item.isNew = item.isNew || (this.isSystemSettings() && isNew);
    item.isSystem = this.isSystemSettings();
    item.isOwner = false;
    item.isCustom = true;
    item.requiresValidation = item.requiresValidation || (item.isNew && !this.isSystemSettings());

    if (isNew && !item.createdBy) {
      item.createdBy = ""; // Should be set by caller
    }
    if (isNew || !item.createdDate) {
      item.createdDate = new Date();
    }

    const config = this.getItemConfig(item.itemType);
    const collection = config.getCollection(settings);

    const existingIndex = collection.findIndex((i) => i.key === item.key);
    const oldItem = existingIndex >= 0 ? collection[existingIndex] : undefined;

    if (existingIndex === -1 || !item.key) {
      collection.push(item);
    } else {
      collection[existingIndex] = item;
    }

    config.setCollection(settings, collection);

    // Audit the change
    this.auditService.auditSettingsItem(
      item,
      oldItem,
      `${config.auditNameSingular} ${isNew ? "Created" : "Updated"}`
    );

    this.fireListeners();
    return { success: true, requiresSave: !suppressAutoSave };
  }

  /**
   * Delete an item by key
   */
  public deleteItem(
    settings: ISessionSettings,
    itemType: ItemTypes,
    itemKey: string,
    suppressAutoSave?: boolean
  ): { success: boolean; requiresSave: boolean; item?: ISettingsItem } {
    const config = this.getItemConfig(itemType);
    const collection = config.getCollection(settings);

    const itemIndex = collection.findIndex((i) => i.key === itemKey);
    if (itemIndex === -1) {
      return { success: false, requiresSave: false };
    }

    const item = collection[itemIndex];

    if (this.isSystemSettings() && item.isDeleted) {
      // Permanently remove
      collection.splice(itemIndex, 1);
    } else {
      // Mark as deleted
      item.isDeleted = true;
    }

    config.setCollection(settings, collection);

    // Audit the deletion
    this.auditService.auditDeleteItem(item, `${config.auditNameSingular} Deleted`);

    this.fireListeners();
    return {
      success: true,
      requiresSave: !suppressAutoSave,
      item
    };
  }

  /**
   * Update multiple items in a collection
   */
  public updateCollection<T extends ISettingsItem>(
    settings: ISessionSettings,
    itemType: ItemTypes,
    items: T[]
  ): void {
    const config = this.getItemConfig(itemType);
    config.setCollection(settings, items);
    this.fireListeners();
  }

  /**
   * Get all items of a specific type
   */
  public getItems<T extends ISettingsItem>(
    settings: ISessionSettings,
    itemType: ItemTypes
  ): T[] {
    const config = this.getItemConfig(itemType);
    return config.getCollection(settings) || [];
  }

  /**
   * Get a single item by key
   */
  public getItemByKey<T extends ISettingsItem>(
    settings: ISessionSettings,
    itemType: ItemTypes,
    key: string
  ): T | undefined {
    const config = this.getItemConfig(itemType);
    const collection = config.getCollection(settings);
    return collection.find((i) => i.key === key) as T | undefined;
  }

  /**
   * Reset items to defaults
   */
  public resetItems(
    settings: ISessionSettings,
    defaultSettings: ISessionSettings,
    itemType: ItemTypes
  ): void {
    const config = this.getItemConfig(itemType);
    const defaultCollection = config.getCollection(defaultSettings);
    config.setCollection(settings, [...defaultCollection]);

    this.auditService.auditOwnerItemAction(`${config.auditNameSingular}s Reset`, undefined);
    this.fireListeners();
  }

  /**
   * Remove override for an item (leaving parent item to set details)
   */
  public removeOverride(
    settings: ISessionSettings,
    item: ISettingsItem
  ): boolean {
    const config = this.getItemConfig(item.itemType);
    const collection = config.getCollection(settings);

    const itemIndex = collection.findIndex((i) => i.key === item.key);
    if (itemIndex === -1) {
      return false;
    }

    // Remove from collection
    collection.splice(itemIndex, 1);
    config.setCollection(settings, collection);

    this.fireListeners();
    return true;
  }

  /**
   * Undelete an item
   */
  public undeleteItem(
    settings: ISessionSettings,
    item: ISettingsItem
  ): boolean {
    const config = this.getItemConfig(item.itemType);
    const collection = config.getCollection(settings);

    const existingItem = collection.find((i) => i.key === item.key);
    if (!existingItem) {
      return false;
    }

    existingItem.isDeleted = false;
    config.setCollection(settings, collection);

    this.fireListeners();
    return true;
  }

  /**
   * Show or hide an item
   */
  public setItemVisibility(
    settings: ISessionSettings,
    item: ISettingsItem,
    show: boolean
  ): boolean {
    const config = this.getItemConfig(item.itemType);
    const collection = config.getCollection(settings);

    const existingItem = collection.find((i) => i.key === item.key);
    if (!existingItem) {
      return false;
    }

    existingItem.hidden = !show;
    config.setCollection(settings, collection);

    this.fireListeners();
    return true;
  }
}
